if (typeof ProgressiveQuantitySelector !== 'function') {
  class ProgressiveQuantitySelector extends HTMLElement {
    constructor() {
      super();
      this.packs = 1;
      this.selectedTierQty = null;
      this.unitPrice = 0;
      this.compareUnitPrice = 0;
      this.installments = 4;
      this.tiers = [];
      this.moneyFormat = '';
      this.saveLabel = '';
      this.eachLabel = '';
      this.installmentsPrefix = '';
      this.installmentsSuffix = '';
      this.compareConnector = '';
      this.stepper = null;
      this.packCountEl = null;
      this.quantityInput = null;
      this.variantPicker = null;
    }

    connectedCallback() {
      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.compareUnitPrice = parseInt(this.dataset.compareUnitPrice, 10) || this.unitPrice;
      this.installments = parseInt(this.dataset.installments, 10) || 4;
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.saveLabel = this.dataset.saveLabel || '';
      this.eachLabel = this.dataset.eachLabel || '';
      this.installmentsPrefix = this.dataset.installmentsPrefix || '';
      this.installmentsSuffix = this.dataset.installmentsSuffix || '';
      this.compareConnector = this.dataset.compareConnector || 'por';
      this.tiers = this.parseTiersFromJson();
      this.selectedTierQty = parseInt(this.dataset.defaultTierQty, 10) || this.tiers[0]?.quantity || 1;

      if (this.tiers.length === 0) return;

      this.quantityInput = this.querySelector('.pqs__quantity-input');
      this.stepper = document.querySelector(`[data-pqs-stepper="${this.dataset.sectionId}"]`);
      this.packCountEl = this.stepper?.querySelector('[data-pqs-pack-count]');
      this.variantPicker = document.querySelector('[data-main-product-variants]');

      this.bindCards();
      this.bindStepper();
      this.bindVariantChange();
      this.applyState(false);
    }

    parseTiersFromJson() {
      const script = this.querySelector('[data-pqs-tiers]');
      if (!script) return [];

      try {
        const parsed = JSON.parse(script.textContent);
        return parsed
          .filter((tier) => tier.quantity > 0)
          .sort((a, b) => a.quantity - b.quantity);
      } catch {
        return [];
      }
    }

    bindCards() {
      this.querySelectorAll('[data-pqs-tier-qty]').forEach((card) => {
        card.addEventListener('click', () => {
          const tierQty = parseInt(card.dataset.pqsTierQty, 10);
          this.selectTier(tierQty, true);
        });
      });
    }

    bindStepper() {
      if (!this.stepper) return;

      const minus = this.stepper.querySelector('.pqs-pack-minus');
      const plus = this.stepper.querySelector('.pqs-pack-plus');

      minus?.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.packs > 1) {
          this.packs -= 1;
          this.applyState(true);
        }
        this.updateStepperButtons();
      });

      plus?.addEventListener('click', (event) => {
        event.preventDefault();
        this.packs += 1;
        this.applyState(true);
        this.updateStepperButtons();
      });

      this.updateStepperButtons();
    }

    bindVariantChange() {
      if (!this.variantPicker) return;

      this.variantPicker.addEventListener('VARIANT_CHANGE', () => {
        const variantData = this.variantPicker.querySelector('[data-js-variant-data]');
        if (!variantData) return;

        const variant = JSON.parse(variantData.textContent);
        if (!variant || !variant.price) return;

        this.unitPrice = variant.price;
        this.dataset.unitPrice = String(variant.price);

        const compare =
          variant.compare_at_price && variant.compare_at_price > variant.price
            ? variant.compare_at_price
            : variant.price;
        this.compareUnitPrice = compare;
        this.dataset.compareUnitPrice = String(compare);

        const available = Boolean(variant.available);
        if (available) {
          this.removeAttribute('hidden');
          if (this.stepper) this.stepper.removeAttribute('hidden');
        } else {
          this.setAttribute('hidden', '');
          if (this.stepper) this.stepper.setAttribute('hidden', '');
        }
        this.updateAllCardPrices();
        this.applyState(false);
      });
    }

    getTierByQty(qty) {
      return this.tiers.find((tier) => tier.quantity === qty) || null;
    }

    getTotalQty() {
      const tier = this.getTierByQty(this.selectedTierQty);
      if (!tier) return this.packs;
      return tier.quantity * this.packs;
    }

    selectTier(tierQty, resetPacks) {
      if (!this.getTierByQty(tierQty)) return;
      this.selectedTierQty = tierQty;
      if (resetPacks) this.packs = 1;
      this.applyState(true);
    }

    applyState(dispatchChange) {
      let totalQty = this.getTotalQty();
      const snapTier = this.getTierByQty(totalQty);

      if (snapTier && totalQty === snapTier.quantity && snapTier.quantity !== this.selectedTierQty) {
        this.selectedTierQty = snapTier.quantity;
        this.packs = 1;
        totalQty = snapTier.quantity;
      }

      this.updateCardSelection();
      this.updateAllCardPrices();

      if (this.quantityInput) {
        this.quantityInput.value = totalQty;
        if (dispatchChange) {
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      if (this.packCountEl) {
        this.packCountEl.textContent = String(this.packs);
      }

      this.updateStepperButtons();
    }

    updateCardSelection() {
      this.querySelectorAll('[data-pqs-tier-qty]').forEach((card) => {
        const isSelected = parseInt(card.dataset.pqsTierQty, 10) === this.selectedTierQty;
        card.classList.toggle('is-selected', isSelected);
        card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
    }

    updateStepperButtons() {
      if (!this.stepper) return;

      const minus = this.stepper.querySelector('.pqs-pack-minus');
      if (minus) {
        minus.classList.toggle('disabled', this.packs <= 1);
      }
    }

    updateAllCardPrices() {
      this.querySelectorAll('[data-pqs-tier-qty]').forEach((card) => {
        const tierQty = parseInt(card.dataset.pqsTierQty, 10);
        const tier = this.getTierByQty(tierQty);
        if (!tier) return;

        const total = Math.max(0, this.unitPrice * tier.quantity - tier.discount);
        const each = Math.round(total / tier.quantity);
        const compareTotal = this.compareUnitPrice * tier.quantity;
        const installment = Math.round(total / this.installments);

        const saveEl = card.querySelector('[data-pqs-save]');
        const priceEl = card.querySelector('[data-pqs-price]');
        const installmentEl = card.querySelector('[data-pqs-installment]');
        const compareEl = card.querySelector('[data-pqs-compare]');
        const compareFromEl = card.querySelector('[data-pqs-compare-from]');
        const compareToEl = card.querySelector('[data-pqs-compare-to]');
        const prefixEl = card.querySelector('.pqs__installments-prefix');
        const suffixEl = card.querySelector('.pqs__installments-suffix');

        if (saveEl) {
          if (tier.discount > 0) {
            saveEl.classList.remove('is-hidden');
            saveEl.innerHTML = this.saveLabel.replace('{{ amount }}', this.formatMoney(tier.discount));
          } else {
            saveEl.classList.add('is-hidden');
          }
        }

        if (priceEl) {
          if (tier.quantity > 1) {
            priceEl.innerHTML = `<span data-pqs-price-each>${this.formatMoney(each)}</span><span class="pqs__price-each">${this.eachLabel}</span>`;
          } else {
            priceEl.innerHTML = `<span data-pqs-price-total>${this.formatMoney(total)}</span>`;
          }
        }

        if (prefixEl && this.installmentsPrefix) {
          prefixEl.textContent = this.installmentsPrefix.replace('{{ count }}', String(this.installments));
        }
        if (suffixEl && this.installmentsSuffix) {
          suffixEl.textContent = this.installmentsSuffix;
        }
        if (installmentEl) {
          installmentEl.textContent = this.formatMoney(installment);
        }

        if (compareEl && compareFromEl && compareToEl) {
          if (compareTotal > total) {
            compareEl.classList.remove('is-hidden');
            compareFromEl.textContent = this.formatMoney(compareTotal);
            compareToEl.textContent = this.formatMoney(total);
          } else {
            compareEl.classList.add('is-hidden');
          }
        }
      });
    }

    formatMoney(cents) {
      if (typeof window.KROWN !== 'undefined' && window.KROWN.settings?.shop_money_format) {
        return this.formatWithShopify(cents, window.KROWN.settings.shop_money_format);
      }
      return this.formatWithShopify(cents, this.moneyFormat);
    }

    formatWithShopify(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';
      const match = formatString.match(placeholderRegex);
      if (!match) return (cents / 100).toFixed(2);

      const formatWithDelimiters = (number, precision, thousands, decimal) => {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) return '0';

        number = (number / 100.0).toFixed(precision);
        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';
        return dollarsAmount + centsAmount;
      };

      let value = '';
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'", '.');
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return formatString.replace(placeholderRegex, value);
    }
  }

  if (typeof customElements.get('progressive-quantity-selector') === 'undefined') {
    customElements.define('progressive-quantity-selector', ProgressiveQuantitySelector);
  }
}
